import React, { useEffect, useState } from "react";
import { Clock } from "lucide-react";
import { useTranslation } from "react-i18next";
import { api } from "@/lib/api";
import { useBilling } from "@/contexts/BillingContext";

/**
 * Small "time saved" widget shown to non-Pro users on the Dashboard, right
 * above the UsageMeter. Quantifies the value received so far — makes the
 * €2 paywall feel like a bargain against the hours already reclaimed.
 *
 * Rules:
 * - Hidden for Pro users (they don't need convincing anymore).
 * - Hidden when the stat is 0 (no summaries yet → nothing to boast about).
 * - Never blocks the render if the API fails.
 */
export const TimeSavedWidget = () => {
  const { t, i18n } = useTranslation();
  const { billing } = useBilling();
  const [stats, setStats] = useState(null);

  useEffect(() => {
    if (!billing || billing.is_pro || billing.is_anonymous) return;
    let cancelled = false;
    api
      .get("/summaries/stats")
      .then(({ data }) => {
        if (!cancelled) setStats(data);
      })
      .catch(() => {
        /* silent — this is a nice-to-have widget, never a blocker */
      });
    return () => {
      cancelled = true;
    };
  }, [billing]);

  if (!billing || billing.is_pro || billing.is_anonymous) return null;
  if (!stats || stats.count === 0 || stats.minutes_saved === 0) return null;

  const minutes = stats.minutes_saved;
  const hours = Math.floor(minutes / 60);
  const remainder = minutes % 60;
  const isFr = i18n.resolvedLanguage === "fr";

  // Human-friendly time — French uses "h" convention, English uses "h min".
  const timeStr =
    hours >= 1
      ? isFr
        ? `${hours}h${String(remainder).padStart(2, "0")}`
        : `${hours}h ${remainder}min`
      : isFr
      ? `${minutes} min`
      : `${minutes} min`;

  const words = Math.round(stats.total_original_chars / 5);

  return (
    <div
      data-testid="time-saved-widget"
      className="border border-[#002FA7] bg-[#002FA7]/5 px-5 py-3 mb-4 flex items-center gap-3"
    >
      <Clock className="w-5 h-5 text-[#002FA7] flex-shrink-0" />
      <div className="flex-1 min-w-0">
        <div className="font-mono uppercase tracking-[0.2em] text-[10px] text-[#002FA7] mb-0.5">
          {t("time_saved.marker")}
        </div>
        <div className="text-sm leading-snug">
          {t("time_saved.body_prefix")}
          <strong className="text-[#002FA7]">{timeStr}</strong>
          {t("time_saved.body_suffix", {
            words: words.toLocaleString(isFr ? "fr-FR" : "en-US"),
            count: stats.count,
          })}
        </div>
      </div>
    </div>
  );
};

export default TimeSavedWidget;
